import '../modals.css';

import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';
import { useAuthContext } from '../../../../common/contexts/useAuthContext';

import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

import { translate } from '../../../../common/language/translations';
import { UserConfig, AccessConfig, AccessType } from '../../../types/user';
import { accessTypeToDefaultAccessConfig, editAccessOptions, editFokusAccessOptions } from '../../../static/constants/accesses';
import { useState } from 'react';
import { updateUserConfig } from '../../../services/firestore/userConfigs';
import { IconButton } from '../../controls/buttons/IconButton';
import { TextButton } from '../../controls/buttons/TextButton';
import { accessConfigsAreEqual, getAccessConfigToStore } from '../../../utils/accessUtils';
import { AccessTable } from '../../tables/accessTable/AccessTable';


interface AccountModalProps {
  user: UserConfig | undefined;
  closeModal: () => void;
  isFokusAccount?: boolean;
  isMobile?: boolean;
}

export const AccountModal: React.FC<AccountModalProps> = ({ user, closeModal, isFokusAccount, isMobile }) => {

  const { currentUser } = useAuthContext();

  const userConfig = useRecoilValue(userConfigState);

  const [selectedAccessType, setSelectedAccessType] = useState<AccessType | undefined>(user?.accessConfig?.accessType);

  const accessOptionTypes = isFokusAccount ? editFokusAccessOptions : editAccessOptions;

  const [accessOptions, setAccessOptions] = useState<Record<AccessType, AccessConfig>>(
    accessOptionTypes.reduce((acc, accessOptionType) => {
      if (accessOptionType === 'custom' && user?.accessConfig?.accessType === 'custom') {
        acc[accessOptionType] = user?.accessConfig;
      }
      else {
        acc[accessOptionType] = accessTypeToDefaultAccessConfig[accessOptionType];
      }
      return acc;
    }, {} as Record<AccessType, AccessConfig>)
  );


  const confirmEdits = () => {
    if (user && selectedAccessType && !accessConfigsAreEqual(user.accessConfig, accessOptions[selectedAccessType])) {
      const accessConfigToStore = getAccessConfigToStore(accessOptions[selectedAccessType]);
      updateUserConfig(user?.email, 'accessConfig', accessConfigToStore, 'AccessEdited', currentUser);
      closeModal();
    }
  };


  return (
    <div className='modal-root-container account-modal-root' style={{ height: 200 + accessOptionTypes.length * 50 }}>

      <IconButton
        icon={<CloseIcon style={{ fontSize: 24 }} />}
        onClick={closeModal}
        size={30}
        style={{ position: 'absolute', top: 12, right: 12 }}
        startWhite={true}
        isMobile={isMobile}
      />

      <div className='modal-root-title'>
        {user ? translate('editAccessFor', userConfig?.language) + ' ' + user.name : ''}
      </div>

      <div className='modal-content-container'>
        <AccessTable
          accessOptions={accessOptions}
          selectedAccessType={selectedAccessType}
          setSelectedAccessType={setSelectedAccessType}
          setCurrentCustomAccessConfig={(accessConfig) => {
            setAccessOptions({ ...accessOptions, custom: accessConfig });
          }}
        />

        <TextButton
          onClick={confirmEdits}
          text={translate('confirmNewAccess', userConfig?.language)}
          icon={<CheckIcon style={{ fontSize: 22 }} />}
          disabled={!selectedAccessType || accessConfigsAreEqual(accessOptions[selectedAccessType], user?.accessConfig)}
          isMobile={isMobile}
        />
      </div>

    </div>
  );
};
